import { cn } from '@quadralize/utils'
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog'
import * as React from 'react'
import { useMobile } from '../../../hooks/use-mobile'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerPortal,
  DrawerTitle,
  DrawerTrigger,
} from '../../overlays/drawer/drawer'
import { buttonVariants } from '../../ui/button/button.variants'

const AlertDialogResponsiveContext = React.createContext<{ isMobile: boolean }>({ isMobile: false })

const AlertDialog = ({ ...props }: React.ComponentProps<typeof AlertDialogPrimitive.Root>) => {
  const isMobile = useMobile()

  return (
    <AlertDialogResponsiveContext.Provider value={{ isMobile }}>
      {isMobile ? <Drawer {...props} /> : <AlertDialogPrimitive.Root {...props} />}
    </AlertDialogResponsiveContext.Provider>
  )
}

const AlertDialogTrigger = ({
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Trigger>) => {
  const { isMobile } = React.useContext(AlertDialogResponsiveContext)

  if (isMobile) {
    return <DrawerTrigger {...props} />
  }

  return <AlertDialogPrimitive.Trigger {...props} />
}

const AlertDialogPortal = ({
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Portal>) => {
  const { isMobile } = React.useContext(AlertDialogResponsiveContext)

  if (isMobile) {
    return <DrawerPortal {...props} />
  }

  return <AlertDialogPrimitive.Portal {...props} />
}

const AlertDialogOverlay = React.forwardRef<
  React.ComponentRef<typeof AlertDialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Overlay
    className={cn(
      'fixed inset-0 z-50 bg-black/40 backdrop-blur-sm data-[state=open]:animate-overlay-show data-[state=closed]:animate-overlay-hide',
      className,
    )}
    {...props}
    ref={ref}
  />
))
AlertDialogOverlay.displayName = AlertDialogPrimitive.Overlay.displayName

const AlertDialogContent = React.forwardRef<
  React.ComponentRef<typeof AlertDialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Content> & {
    closeOnOverlayClick?: boolean
  }
>(({ className, closeOnOverlayClick = true, ...props }, ref) => {
  const { isMobile } = React.useContext(AlertDialogResponsiveContext)

  if (isMobile) {
    return (
      <DrawerContent ref={ref} className={className} {...props}>
        {props.children}
      </DrawerContent>
    )
  }

  return (
    <AlertDialogPortal>
      <AlertDialogOverlay />
      <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4">
        <AlertDialogPrimitive.Content
          ref={ref}
          className={cn(
            'relative z-50 flex flex-col w-full max-w-lg overflow-hidden border-t border-border-strong bg-bg-elevated shadow-md data-[state=open]:animate-content-show-mobile data-[state=closed]:animate-content-hide-mobile sm:rounded-xl sm:border sm:data-[state=open]:animate-content-show sm:data-[state=closed]:animate-content-hide rounded-t-2xl max-h-[90vh] sm:max-h-[85vh]',
            className,
          )}
          {...({
            onPointerDownOutside: (event: Event) => {
              if (!closeOnOverlayClick) {
                event.preventDefault()
              }
            },
            onInteractOutside: (event: Event) => {
              if (!closeOnOverlayClick) {
                event.preventDefault()
              }
            },
          } as React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Content> & {
            onPointerDownOutside?: (event: Event) => void
            onInteractOutside?: (event: Event) => void
          })}
          {...props}
        />
      </div>
    </AlertDialogPortal>
  )
})
AlertDialogContent.displayName = AlertDialogPrimitive.Content.displayName

const AlertDialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
  const { isMobile } = React.useContext(AlertDialogResponsiveContext)

  if (isMobile) {
    return <DrawerHeader className={className} {...props} />
  }

  return (
    <div
      className={cn('flex flex-col gap-2 text-center sm:text-left flex-none p-6', className)}
      {...props}
    />
  )
}
AlertDialogHeader.displayName = 'AlertDialogHeader'

const AlertDialogBody = ({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  return (
    <div
      className={cn('flex-1 w-full min-h-0 overflow-y-auto overflow-x-hidden', className)}
      {...props}
    >
      <div className="px-6 py-2">{children}</div>
    </div>
  )
}
AlertDialogBody.displayName = 'AlertDialogBody'

const AlertDialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
  const { isMobile } = React.useContext(AlertDialogResponsiveContext)

  if (isMobile) {
    return <DrawerFooter className={className} {...props} />
  }

  return (
    <div
      className={cn(
        'flex flex-col-reverse sm:flex-row sm:justify-end gap-2 flex-none p-6 pt-0',
        className,
      )}
      {...props}
    />
  )
}
AlertDialogFooter.displayName = 'AlertDialogFooter'

const AlertDialogTitle = React.forwardRef<
  React.ComponentRef<typeof AlertDialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Title>
>(({ className, ...props }, ref) => {
  const { isMobile } = React.useContext(AlertDialogResponsiveContext)

  if (isMobile) {
    return <DrawerTitle ref={ref} className={className} {...props} />
  }

  return (
    <AlertDialogPrimitive.Title
      ref={ref}
      className={cn('text-lg font-semibold text-text-primary', className)}
      {...props}
    />
  )
})
AlertDialogTitle.displayName = AlertDialogPrimitive.Title.displayName

const AlertDialogDescription = React.forwardRef<
  React.ComponentRef<typeof AlertDialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Description>
>(({ className, ...props }, ref) => {
  const { isMobile } = React.useContext(AlertDialogResponsiveContext)

  if (isMobile) {
    return <DrawerDescription ref={ref} className={className} {...props} />
  }

  return (
    <AlertDialogPrimitive.Description
      ref={ref}
      className={cn('text-sm text-text-secondary', className)}
      {...props}
    />
  )
})
AlertDialogDescription.displayName = AlertDialogPrimitive.Description.displayName

const AlertDialogAction = React.forwardRef<
  React.ComponentRef<typeof AlertDialogPrimitive.Action>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Action>
>(({ className, ...props }, ref) => {
  const { isMobile } = React.useContext(AlertDialogResponsiveContext)

  if (isMobile) {
    return (
      <DrawerClose ref={ref} asChild>
        <button className={cn(buttonVariants(), className)} {...props} />
      </DrawerClose>
    )
  }

  return (
    <AlertDialogPrimitive.Action ref={ref} className={cn(buttonVariants(), className)} {...props} />
  )
})
AlertDialogAction.displayName = AlertDialogPrimitive.Action.displayName

const AlertDialogCancel = React.forwardRef<
  React.ComponentRef<typeof AlertDialogPrimitive.Cancel>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Cancel>
>(({ className, ...props }, ref) => {
  const { isMobile } = React.useContext(AlertDialogResponsiveContext)

  if (isMobile) {
    return (
      <DrawerClose ref={ref} asChild>
        <button
          type="button"
          className={cn(buttonVariants({ variant: 'neutral' }), 'mt-2 sm:mt-0', className)}
          {...props}
        />
      </DrawerClose>
    )
  }

  return (
    <AlertDialogPrimitive.Cancel
      ref={ref}
      className={cn(buttonVariants({ variant: 'neutral' }), 'mt-2 sm:mt-0', className)}
      {...props}
    />
  )
})
AlertDialogCancel.displayName = AlertDialogPrimitive.Cancel.displayName

export {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
  AlertDialogTrigger,
}
